import { useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";

export const pct = (u, a) => (u != null && a ? Math.round((u / a) * 100) : null);
export const fmtGb = (v) => (v == null ? "—" : v < 1 ? `${Math.round(v * 1024)} MB` : `${v.toFixed(2)} GB`);
export const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export function level(p, limits) {
  if (p == null) return { label: "No limit set", tone: "text-slate-400", bar: "bg-slate-300" };
  if (p >= limits.critical_pct) return { label: "Near capacity", tone: "text-rose-500", bar: "bg-rose-500" };
  if (p >= limits.warn_pct) return { label: "Approaching limit", tone: "text-amber-500", bar: "bg-amber-500" };
  return { label: "Normal", tone: "text-emerald-500", bar: "bg-emerald-500" };
}

function Spark({ data, color }) {
  if (!data || data.length < 2) return <div className="h-9 flex items-center text-[10px] text-slate-400">Collecting history…</div>;
  return (
    <div className="h-9">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <YAxis hide domain={["auto", "auto"]} />
          <Tooltip contentStyle={{ fontSize: 10, padding: "2px 6px" }} labelFormatter={(i) => when(data[i]?.ts)} formatter={(v) => [v, ""]} />
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// One KPI: value, allocation, capacity bar, 7-day sparkline, data-source label.
export function Kpi({ title, used, alloc, unit = "", fmt, series, source, note, limits, testId, color = "#327de3" }) {
  const [open, setOpen] = useState(false);
  const p = pct(used, alloc);
  const lv = level(p, limits);
  const f = fmt || ((v) => (v == null ? "—" : `${v}${unit}`));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 min-w-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 leading-tight">{title}</div>
      <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500" title={note}>{source}</span>
      <div className="flex items-baseline gap-1.5 mt-0.5 flex-wrap">
        <span className="text-lg font-bold text-slate-900 tabular-nums whitespace-nowrap" data-testid={`${testId}-used`}>{used == null ? "Not available" : f(used)}</span>
        <span className="text-[11px] text-slate-500 whitespace-nowrap">/ {alloc == null ? "limit unknown" : f(alloc)}{p != null ? ` · ${p}%` : ""}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className={`h-full ${lv.bar} transition-[width] duration-500`} style={{ width: `${Math.min(100, p ?? 0)}%` }} /></div>
      <div className={`text-[10px] mt-0.5 font-semibold ${lv.tone}`}>{lv.label}{p != null && alloc != null && used != null ? ` · ${f(Math.max(0, +(alloc - used).toFixed(3)))} headroom` : ""}</div>
      <Spark data={series} color={color} />
      {note && <button onClick={() => setOpen(!open)} className="text-[10px] text-slate-400 hover:text-slate-600" data-testid={`${testId}-note-toggle`}>{open ? note : "What is this?"}</button>}
    </div>
  );
}
